// frontend/src/components/ChatInterface.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { ExtendedMessage, ModelId } from '../types';
import Avatar from './Avatar';

interface ChatInterfaceProps {
    inputValue: string;
    setInputValue: (value: string) => void;
    messages: ExtendedMessage[];
    onSendMessage: (message: string) => Promise<void>;
    isLoading?: boolean;
    theme: 'light' | 'dark' | 'neon' | 'pastel';
    modelId: ModelId;
    socket?: Socket | null;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
    inputValue,
    setInputValue,
    messages,
    onSendMessage,
    isLoading = false,
    theme,
    modelId,
    socket,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [displayedMessages, setDisplayedMessages] = useState<ExtendedMessage[]>([]);
    const [isModelLoading, setIsModelLoading] = useState<boolean>(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState<boolean>(true);

    useEffect(() => {
        console.log('ChatInterface re-rendu');
    });

    useEffect(() => {
        if (!socket) return;

        const handleModelLoading = () => {
            console.log('Événement modelLoading reçu');
            setIsModelLoading(true);
        };

        socket.on('modelLoading', handleModelLoading);

        return () => {
            socket.off('modelLoading', handleModelLoading);
        };
    }, [socket]);

    useEffect(() => {
        console.log('Mise à jour de displayedMessages, messages.length:', messages.length);
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.sender === 'ia' && !lastMessage.final) {
            setDisplayedMessages((prev) => {
                const lastDisplayed = prev[prev.length - 1];
                if (lastDisplayed && lastDisplayed.sender === 'ia' && !lastDisplayed.final) {
                    return [...prev.slice(0, -1), { ...lastDisplayed, content: lastMessage.content }];
                }
                return [...prev, lastMessage];
            });
        } else {
            setDisplayedMessages(messages);
            if (lastMessage?.final && isModelLoading) {
                setIsModelLoading(false);
            }
        }
    }, [messages, isModelLoading]);

    const scrollToBottom = useCallback(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [displayedMessages, scrollToBottom]);

    const handleSend = useCallback(async () => {
        if (isSending || isLoading) return;
        const trimmedInput = inputValue.trim();
        if (trimmedInput) {
            setIsSending(true);
            setInputValue(trimmedInput);
            setInputValue('');
            await onSendMessage(trimmedInput);
            setIsSending(false);
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }
    }, [inputValue, isSending, isLoading, onSendMessage, setInputValue]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend]
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setInputValue(e.target.value);
        },
        [setInputValue]
    );

    const toggleKeyboard = () => {
        setIsKeyboardOpen((prev) => !prev);
        if (!isKeyboardOpen && textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const handleMouseOverMessages = () => {
        console.log('Survol de chat-messages');
    };

    return (
        <div className="chat">
            <div className="chat-messages" onMouseOver={handleMouseOverMessages}>
                {displayedMessages.length === 0 && !isModelLoading ? (
                    <p className="chat__empty-state">Commencez une conversation...</p>
                ) : (
                    <>
                        {displayedMessages.map((msg) => (
                            <div
                                key={msg.conversationId ? `${msg.conversationId}-${msg.sender}-${msg.timestamp}` : `${msg.sender}-${msg.timestamp}`}
                                className={`chat-message ${msg.sender === 'user' ? 'user-message' : msg.sender === 'system' ? 'message--system' : 'ia-message'} ${msg.sender === 'ia' && !msg.final ? 'typing' : ''}`}
                            >
                                <Avatar
                                    sender={msg.sender === 'system' ? 'ia' : msg.sender}
                                    modelId={msg.sender === 'ia' ? modelId : undefined}
                                    size="sm"
                                    theme={theme}
                                />
                                <span className="break-words whitespace-pre-wrap">{msg.content}</span>
                                {!msg.final && msg.sender === 'ia' && (
                                    <span className="typing-indicator">
                                        <span className="typing-dots">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </span>
                                    </span>
                                )}
                            </div>
                        ))}
                        {isModelLoading && (
                            <div className="chat-message ia-message">
                                <Avatar sender="ia" modelId={modelId} size="sm" theme={theme} />
                                <span className="break-words whitespace-pre-wrap">
                                    Modèle en cours de chargement...
                                </span>
                            </div>
                        )}
                        <div ref={scrollRef} />
                    </>
                )}
            </div>
            <div className="chat-input">
                <button
                    onClick={toggleKeyboard}
                    className="toggle-keyboard"
                    aria-label={isKeyboardOpen ? 'Fermer le clavier' : 'Ouvrir le clavier'}
                >
                    ⌨️
                </button>
                {isKeyboardOpen && (
                    <>
                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Posez votre question..."
                            disabled={isLoading}
                            className="chat-input__textarea"
                            rows={1}
                            style={{ minHeight: '40px' }}
                            aria-label="Champ de saisie de message"
                        />
                        <button
                            onClick={handleSend}
                            className="chat-input__button"
                            disabled={isLoading || !inputValue.trim()}
                            aria-label="Envoyer le message"
                        >
                            Envoyer
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default React.memo(ChatInterface);